import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { yupErrorMsg } from '../../../../components/atoms/const';
import api from '../../../../components/api';
import useDateRangePicker from '../../../../components/organisms/forms/date-range-picker/reusable-func';

function useIdPartner() {
  const { partnerId } = useParams();
  // Navigate Handler
  const navigation = useNavigate();
  const appendZero = (monthDay: number): string => {
    let month = `${monthDay}`;
    if (monthDay < 10) {
      month = `0${monthDay}`;
    }

    return month;
  };
  const onNavigateToEditPartnerDetail = () => navigation(`/partners/add/${partnerId}`);
  const onNavigateToLanding = () => navigation('/partners');
  const onNavigateToEditPartner = () => navigation(`/partners/edit/${partnerId}`);
  const onNavigateToEditContactPerson = (contactId: number) => navigation(`/partners/edit/${partnerId}/${contactId}`);
  const [startDate, setStartDate] = React.useState<string>();
  const [endDate, setEndDate] = React.useState<string>();

  // Get Initial Data
  // 1. Get Partner by url params id
  const {
    data, isLoading, refetch: refetchPartner,
  } = useQuery(
    ['getById', 'partner', partnerId],
    () => api.partner.getById(partnerId!),
    {
      enabled: !!partnerId,
    },
  );
  const partnerById = React.useMemo(() => data?.data?.data, [data]);
  // 2. Get Commission Report
  const { data: monthlyCommissionRes, isLoading: commissionLoad } = useQuery(
    ['getMonthlyCommissionByDateRangeAndId', 'partner', partnerId, startDate, endDate],
    () => api.partner.getMonthlyCommissionByDateRangeAndId(startDate!, endDate!, partnerId!),
    {
      enabled: !!partnerId,
    },
  );

  const monthlyCommission = React.useMemo(() => monthlyCommissionRes?.data?.data, [monthlyCommissionRes]);

  const {
    data: partnerCommissionRes, isLoading: partnerCommissionLoad, isFetching: partnerCommissionFetch, refetch: refetchCommission,
  } = useQuery(
    ['getPartnerCommissions', 'partner', partnerId],
    () => api.partner.getPartnerCommissions(partnerId!),
    {
      enabled: !!partnerId,
    },
  );

  const partnerCommissions = React.useMemo(() => partnerCommissionRes?.data?.data, [partnerCommissionRes]);

  // Delete Handler
  const [result, setResult] = React.useState<any>();
  // 1. Delete Partner
  const { mutate: deletePartner, isLoading: deletePartnerLoad } = useMutation(api.partner.remove, {
    onMutate: (x) => console.log('onMutate', x),
    onSuccess: (res) => {
      console.log('delete partner result', res);
      setResult(res);
    },
    onError: (err) => {
      console.log('delete partner error', err);
      setResult(err);
    },
  });
  const handleDeletePartner = () => {
    deletePartner(Number(partnerId));
  };
  // 2. Delete Contact Person
  const { mutate: deleteContactPerson, isLoading: deleteCPLoad } = useMutation(api.clients.deleteContact, {
    onMutate: (x) => console.log('onMutate', x),
    onSuccess: (res) => {
      console.log('delete cp result', res);
      setResult(res);
    },
    onError: (err) => {
      console.log('delete cp error', err);
      setResult(err);
    },
  });
  const handleDeleteContactPerson = (contactId: number) => {
    deleteContactPerson(contactId);
  };

  const onAfterDeletePartner = async () => {
    setResult(undefined);
    onNavigateToLanding();
  };

  const { mutate: saveCommission, isLoading: saveCommissionLoad } = useMutation(api.partner.addCommission, {
    onMutate: (x) => console.log('onMutate', x),
    onSuccess: (res) => {
      console.log('Saved commission result', res);
      setResult(false);
      refetchCommission().catch((e) => console.log(e));
    },
    onError: (err) => {
      console.log('Saved commission result error', err);
      setResult(err);
    },
  });

  const handleSaveCommission = async () => {
    console.log('Saving commission.....');
    if (monthlyCommission) {
      saveCommission({
        partnerId: Number(partnerId),
        salesFrom: startDate!,
        salesTo: endDate!,
        totalCount: monthlyCommission.totalSummary.quantity,
        totalSales: monthlyCommission.totalSummary.amount,
        totalCommission: monthlyCommission.totalSummary.totalCommission,
      });
    }
  };

  const { handleChangeStartDate, handleChangeEndDate, selectedDate } = useDateRangePicker();
  const [show, setShow] = React.useState<boolean>();

  const onAfterDeleteContactPerson = async () => {
    setResult(undefined);
    refetchPartner().catch((e) => console.log(e));
  };

  const handleCommissionReport = async () => {
    console.log('Generating commission Report:');
    console.log(partnerId);
    console.log(selectedDate.start);
    const selectedStartDate = new Date(selectedDate.start);
    const startDateStr = `${selectedStartDate.getFullYear()}${appendZero(selectedStartDate.getMonth() + 1)}${appendZero(selectedStartDate.getDate())}`;
    console.log(startDateStr);
    setStartDate(startDateStr);
    console.log('End Date:');
    console.log(selectedDate.end);
    const selectedEndDate = new Date(selectedDate.end);
    const endDateStr = `${selectedEndDate.getFullYear()}${appendZero(selectedEndDate.getMonth() + 1)}${appendZero(selectedEndDate.getDate())}`;
    setEndDate(endDateStr);
    console.log(endDateStr);
  };

  const onAfterGenerateCommission = async () => {
    console.log('Cancel clicked');
    setResult(undefined);
    refetchPartner().catch((e) => console.log(e));
  };

  const { mutate: sendCommissionPayment, isLoading: sendPaymentLoad } = useMutation(api.partner.commissionPayment, {
    onMutate: (variables) => console.log(variables),
    onSuccess: (res) => {
      console.log(res);
      setResult(res);
    },
    onError: (error) => {
      console.log(error);
      setResult(error);
    },
  });

  const yupSchema = yup.object({
    paymentAmount: yup.number().min(0, yupErrorMsg.COMMISSION_AMOUNT_POSITIVE).required(yupErrorMsg.COMMISSION_AMOUNT_EMPTY),
  });

  const formik = useFormik({
    initialValues: {
      paymentAmount: 0,
      id: 0,
    },
    validationSchema: yupSchema,
    onSubmit: (values) => sendCommissionPayment(values),
  });

  const afterSendCommissionPayment = () => {
    refetchCommission().catch((e) => console.log(e));
    setResult(undefined);
  };

  return {
    partnerById: {
      data: partnerById,
      isLoading,
    },
    modalPartner: {
      show,
      result,
      isLoading: deletePartnerLoad,
      handleDelete: handleDeletePartner,
      onAfterDelete: onAfterDeletePartner,
    },
    modalContactPerson: {
      result,
      isLoading: deleteCPLoad,
      handleDelete: handleDeleteContactPerson,
      onAfterDelete: onAfterDeleteContactPerson,
    },
    modalDatePicker: {
      result,
      isLoading: commissionLoad,
      handleDelete: handleCommissionReport,
      onAfterDelete: onAfterGenerateCommission,
    },
    modalClientCommission: {
      result,
      isLoading: commissionLoad,
      handleDelete: handleSaveCommission,
      onAfterDelete: onAfterGenerateCommission,
    },
    navigate: {
      editPartnerDetail: onNavigateToEditPartnerDetail,
      landing: onNavigateToLanding,
      editContactPerson: onNavigateToEditContactPerson,
      editPartner: onNavigateToEditPartner,
    },
    monthlyCommission: {
      data: monthlyCommission,
      isLoading: commissionLoad,
    },
    partnerCommissions: {
      data: partnerCommissions,
      isLoading: partnerCommissionLoad || partnerCommissionFetch,
    },
    formik,
    paymentModal: {
      load: sendPaymentLoad,
      after: afterSendCommissionPayment,
      result,
    },
    handleChangeStartDate,
    handleChangeEndDate,
    selectedDate,
  };
}

export default useIdPartner;
